/**
 * Data Access Layer - Fine-Tuning Repository
 * Handles all data access operations for fine-tuning
 */

import api from '../interceptors/interceptor';
import { API_ROUTES } from '../constants/apiRoutes';

export interface UploadDatasetResponse {
  success: boolean;
  message: string;
  datasetId?: string;
  validationStatus?: string;
}

export interface TrainingFile {
  filename: string;
  path: string;
  relative_path: string;
  size_mb: number;
  line_count: number;
}

export interface ListTrainingFilesResponse {
  instance_id: string;
  pvc_name: string;
  pod_name: string;
  training_folder: string;
  full_path: string;
  files: TrainingFile[];
  file_count: number;
}

export interface TrainingJob {
  name: string;
  namespace: string;
  status: string;
  active: number;
  succeeded: number;
  failed: number;
  creation_time: string;
  start_time: string;
  completion_time: string | null;
  duration: string | null;
  labels: {
    [key: string]: string;
  };
}

export interface ListTrainingJobsResponse {
  namespace: string;
  total_jobs: number;
  jobs: TrainingJob[];
}

export interface StartTrainingJobRequest {
  instance_id: string;
  training_data_path: string;
  epochs: number;
  batch_size: number;
  learning_rate: number;
  warmup_ratio: number;
  gradient_accumulation_steps: number;
  weight_decay: number;
  enable_fp16: boolean;
  save_steps: number;
  eval_steps: number;
  optimizer: string;
  lora_dropout: number;
  lora_rank: number;
  lora_alpha: number;
  bit4_quantization: boolean;
  bit4_quantization_type: string;
  bit4_compute_dtype: string;
  fine_tuned_model_path: string;
}

export interface StartTrainingJobResponse {
  success: boolean;
  message: string;
  job_name?: string;
  namespace?: string;
  status?: string;
}

export interface DeployFineTunedModelRequest {
  instance_id: string;
  fine_tuned_model_path: string;
  file_share_path: string;
}

export interface DeployFineTunedModelResponse {
  success: boolean;
  message: string;
  deployment_id?: string;
  status?: string;
}

export interface CreateModelConfigRequest {
  instance_id: string;
  fine_tuned_model_path: string;
  file_share_path: string;
}

export interface CreateModelConfigResponse {
  success: boolean;
  message: string;
  config_id?: string;
  status?: string;
}

export class FineTuningRepository {
  private static instance: FineTuningRepository;

  private constructor() {}

  /**
   * Singleton pattern - ensure only one instance exists
   */
  public static getInstance(): FineTuningRepository {
    if (!FineTuningRepository.instance) {
      FineTuningRepository.instance = new FineTuningRepository();
    }
    return FineTuningRepository.instance;
  }

  /**
   * Upload dataset for fine-tuning using FormData
   */
  public async uploadDataset(formData: FormData): Promise<UploadDatasetResponse> {
    try {
      const response = await api.post<UploadDatasetResponse>(
        API_ROUTES.FINE_TUNE_UPLOAD_DATA,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to upload dataset');
    }
  }

  /**
   * List training files for a specific instance
   */
  public async listTrainingFiles(instanceId: string): Promise<ListTrainingFilesResponse> {
    try {
      const response = await api.get<ListTrainingFilesResponse>(
        API_ROUTES.FINE_TUNE_LIST_TRAINING_FILES(instanceId)
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch training files');
    }
  }

  /**
   * Get training jobs for a namespace
   */
  public async getTrainingJobs(namespace: string): Promise<ListTrainingJobsResponse> {
    try {
      const response = await api.get<ListTrainingJobsResponse>(
        API_ROUTES.FINE_TUNE_JOBS(namespace)
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch training jobs');
    }
  }

  /**
   * Start a new training job
   */
  public async startTrainingJob(request: StartTrainingJobRequest): Promise<StartTrainingJobResponse> {
    try {
      const response = await api.post<StartTrainingJobResponse>(
        API_ROUTES.FINE_TUNE_START,
        request
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to start training job');
    }
  }

  /**
   * Deploy a fine-tuned model
   */
  public async deployFineTunedModel(request: DeployFineTunedModelRequest): Promise<DeployFineTunedModelResponse> {
    try {
      const response = await api.post<DeployFineTunedModelResponse>(
        API_ROUTES.FINE_TUNE_DEPLOY,
        request
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to deploy fine-tuned model');
    }
  }

  /**
   * Create model configuration from fine-tuned model
   */
  public async createModelConfig(request: CreateModelConfigRequest): Promise<CreateModelConfigResponse> {
    try {
      const response = await api.post<CreateModelConfigResponse>(
        API_ROUTES.FINE_TUNE_CREATE_MODEL_CONFIG,
        request
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create model configuration');
    }
  }
}
